
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { BrandSettings, BrandSettingsForm } from './types';
import { uploadLogo } from '../storage/logoUploadService';

export async function fetchBrandSettings(userId: string): Promise<BrandSettings | null> {
  try {
    // Use maybeSingle instead of single to avoid errors
    const { data, error } = await supabase
      .from('brand_settings')
      .select('*')
      .eq('user_id', userId)
      .maybeSingle();
    
    if (error) {
      console.error('Error fetching brand settings:', error);
      return null;
    }
    
    // Ensure scope is properly typed
    if (data) {
      data.scope = (data.scope as "homeOnly" | "global") || "global";
    }
    
    return data as BrandSettings;
  } catch (error) {
    console.error('Error in fetchBrandSettings:', error);
    return null;
  }
}

export async function updateBrandSettings(
  userId: string,
  settings: BrandSettingsForm
): Promise<BrandSettings | null> {
  try {
    console.log('Updating brand settings for userId:', userId);
    
    // Handle logo upload if it's a data URL
    let logoUrl = settings.logo;
    if (settings.logo && settings.logo.startsWith('data:')) {
      // Upload the logo to storage and get the public URL
      const uploadedUrl = await uploadLogo(settings.logo, userId);
      if (uploadedUrl) {
        logoUrl = uploadedUrl;
      }
    }
    
    // First, check if brand settings exist
    const { data: existingSettings } = await supabase
      .from('brand_settings')
      .select('*')
      .eq('user_id', userId)
      .maybeSingle();
    
    if (!existingSettings) {
      console.log('No existing brand settings found, creating new settings');
      
      // Get the current authenticated user for RLS policy
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user || user.id !== userId) {
        console.error('Authentication mismatch: current user does not match target user ID');
        toast.error('Authentication error. Please log in again.');
        return null;
      }
      
      // Insert new settings
      const { data: insertedData, error: insertError } = await supabase
        .from('brand_settings')
        .insert({
          user_id: user.id,
          name: settings.name,
          logo: logoUrl,
          primary_color: settings.primary_color,
          secondary_color: settings.secondary_color,
          accent_color: settings.accent_color,
          scope: settings.scope || 'global'
        })
        .select()
        .single();
      
      if (insertError) {
        console.error('Error creating brand settings:', insertError);
        toast.error('Failed to create brand settings');
        return null;
      }
      
      toast.success('Brand settings created successfully');
      // Ensure scope is properly typed
      if (insertedData) {
        insertedData.scope = (insertedData.scope as "homeOnly" | "global") || "global";
      }
      return insertedData as BrandSettings;
    }
    
    // Update existing settings
    console.log('Existing brand settings found, updating');
    const { data, error } = await supabase
      .from('brand_settings')
      .update({
        name: settings.name,
        logo: logoUrl,
        primary_color: settings.primary_color,
        secondary_color: settings.secondary_color,
        accent_color: settings.accent_color,
        scope: settings.scope || existingSettings.scope || 'global'
      })
      .eq('user_id', userId)
      .select()
      .single();
    
    if (error) {
      console.error('Error updating brand settings:', error);
      toast.error('Failed to update brand settings');
      return null;
    }
    
    toast.success('Brand settings updated successfully');
    // Ensure scope is properly typed
    if (data) {
      data.scope = (data.scope as "homeOnly" | "global") || "global";
    }
    return data as BrandSettings;
  } catch (error) {
    console.error('Error in updateBrandSettings:', error);
    toast.error('An unexpected error occurred');
    return null;
  }
}
